import { queryOptions, UseQueryOptions } from '@tanstack/react-query'

import { TypedDocumentString } from '@/gql/graphql'

import { gqlFetch } from './gql-fetch'

interface CreateGqlArgs<TResult, TVariables> {
  key: string
  query: TypedDocumentString<TResult, TVariables>
}

function createGqlQuery<
  TResult,
  TVariables,
  TVariablesParam extends TVariables extends Record<string, never>
    ? undefined
    : TVariables = TVariables extends Record<string, never>
    ? undefined
    : TVariables,
>(args: CreateGqlArgs<TResult, TVariables>) {
  const { key, query } = args

  const fetchData = async (variables?: TVariablesParam) =>
    gqlFetch({ query, variables })

  return {
    query,
    api: (params?: TVariablesParam): Promise<TResult> => fetchData(params),

    /** Query options for react-query */
    qry: (variables?: TVariablesParam) => {
      return queryOptions({
        queryKey: [key, { variables }],
        queryFn: () => fetchData(variables),
      })
    },
    key,
    /** Type inference helpers */
    infer: {
      variables: {} as TVariablesParam,
      output: {} as TResult,
    },
  }
}

/**
 * Type helpers for inferring input and output types from createGqlQuery
 */
type InferVariables<T> = T extends { infer: { input: infer I } } ? I : never
type InferOutput<T> = T extends { infer: { output: infer O } } ? O : never

export { createGqlQuery, type InferVariables, type InferOutput }
